import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import UserService from "services/User";

const savedFiltersKey = tableName => ["saved-filters", tableName];

const handleFetchSaveFilters = async ({ queryKey = [] }) => {
  const [, tableName] = queryKey;
  if (!tableName) {
    throw new Error("tableName is required");
  }
  const response = await UserService.getUserSettings();
  const tableSettings = get(
    response,
    `user_settings.preferences.tables.${tableName}`,
    {}
  );
  return tableSettings.search_params ?? [];
};

export function useSaveFilters({ tableName }) {
  const { data, isLoading } = useQuery({
    queryKey: savedFiltersKey(tableName),
    queryFn: handleFetchSaveFilters,
    enabled: !!tableName,
    initialData: [],
  });

  return {
    data,
    isLoading,
  };
}

export function useCreateSavedFilter({ tableName }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, description, state }) => {
      const current =
        queryClient.getQueryData(savedFiltersKey(tableName)) ?? [];
      // Names are the row identity (combobox key, delete lookup), so they must
      // stay unique within a table.
      if (current.some(f => f.name === name)) {
        throw new Error(`A filter named "${name}" already exists`);
      }
      // `isNewPattern` distinguishes these from the legacy AdvancedSearch
      // entries that share the same `search_params` array, so each UI only
      // renders the filters it can apply.
      const next = [
        ...current,
        { name, description, state, isNewPattern: true },
      ];
      await UserService.updateUserTableSettings(
        tableName,
        "search_params",
        next
      );
    },
    onSuccess: (_data, { name }) => {
      queryClient.invalidateQueries({ queryKey: savedFiltersKey(tableName) });
      toast.success(`Saved "${name}"`);
    },
    onError: error => {
      toast.error(error.message || "Failed to save filter");
    },
  });
}

export function useDeleteSavedFilter({ tableName }) {
  const queryClient = useQueryClient();

  return useMutation({
    // Saved filters live in the user's `search_params` JSON, so a delete is a
    // read-filter-write of the whole array rather than a targeted DELETE.
    mutationFn: async filter => {
      const current =
        queryClient.getQueryData(savedFiltersKey(tableName)) ?? [];
      const next = current.filter(f => f.name !== filter.name);
      await UserService.updateUserTableSettings(
        tableName,
        "search_params",
        next
      );
    },
    onSuccess: (_data, filter) => {
      queryClient.invalidateQueries({ queryKey: savedFiltersKey(tableName) });
      toast.success(`Deleted "${filter.name}"`);
    },
    onError: error => {
      toast.error(error.message || "Failed to delete filter");
    },
  });
}
